import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";

export default function SimulationMenu({
  idx,
  handleEdit,
  handleDuplicate,
  handleDelete,
  deleteDisabled = false,
}: {
  idx: number;
  handleEdit?: (idx: number) => void;
  handleDuplicate?: (idx: number) => void;
  handleDelete?: (idx: number) => void;
  deleteDisabled?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const availableHandlers = [
    handleEdit ? "edit" : null,
    handleDuplicate ? "duplicate" : null,
    handleDelete ? "delete" : null,
  ].filter(Boolean);

  if (availableHandlers.length === 0) {
    return null;
  }

  const EditIconButton: React.FC = () => (
    <IconButton aria-label="edit" size="large" onClick={() => handleEdit!(idx)}>
      <EditIcon />
    </IconButton>
  );

  const DuplicateIconButton: React.FC = () => (
    <IconButton
      aria-label="duplicate"
      size="large"
      onClick={() => handleDuplicate!(idx)}
    >
      <ContentCopyIcon />
    </IconButton>
  );

  const DeleteIconButton: React.FC = () => (
    <IconButton
      aria-label="delete"
      size="large"
      onClick={() => handleDelete!(idx)}
      sx={{ color: "error.main" }}
      disabled={deleteDisabled}
    >
      <DeleteIcon />
    </IconButton>
  );

  // Separate menu item components (MenuItem variants)
  const EditMenuItem: React.FC = () => (
    <MenuItem
      onClick={() => {
        handleEdit!(idx);
        handleMenuClose();
      }}
    >
      <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
    </MenuItem>
  );

  const DuplicateMenuItem: React.FC = () => (
    <MenuItem
      onClick={() => {
        handleDuplicate!(idx);
        handleMenuClose();
      }}
    >
      <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Duplicate
    </MenuItem>
  );

  const DeleteMenuItem: React.FC = () => (
    <MenuItem
      onClick={() => {
        handleDelete!(idx);
        handleMenuClose();
      }}
      sx={{ color: "error.main" }}
      disabled={deleteDisabled}
    >
      <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
    </MenuItem>
  );

  if (availableHandlers.length === 1) {
    const only = availableHandlers[0];
    if (only === "edit") return <EditIconButton />;
    if (only === "duplicate") return <DuplicateIconButton />;
    if (only === "delete") return <DeleteIconButton />;
  }

  return (
    <>
      <IconButton aria-label="more" size="large" onClick={handleMenuOpen}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {handleEdit && <EditMenuItem />}
        {handleDuplicate && <DuplicateMenuItem />}
        {handleDelete && <DeleteMenuItem />}
      </Menu>
    </>
  );
}
